import { Flex, StatusIndicator, Text, Tooltip, TooltipTrigger } from '@chatsdet/ui';
import type { StatusIndicatorProps } from '@chatsdet/ui';
import {
  useOutageStatus,
  type OverallStatus,
  type OutageIncident,
} from '../../hooks/useOutageStatus';

const STATUS_PAGE_URL =
  (import.meta.env.VITE_STATUS_PAGE_URL as string | undefined) ?? 'https://status.runcofactor.com';

/** Map an overall status onto a @chatsdet/ui semantic dot variant (theme-aware). */
const VARIANT_BY_OVERALL: Record<OverallStatus, StatusIndicatorProps['variant']> = {
  operational: 'positive',
  maintenance: 'info',
  degraded: 'notice',
  partial_outage: 'negative',
  major_outage: 'negative',
};

const INCIDENT_STATUS_LABEL: Record<OutageIncident['status'], string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

/**
 * Compact header indicator that appears ONLY during an active outage. Renders
 * nothing when systems are operational, while loading, or on any fetch error
 * (the underlying API already fails silent). Links out to the public status
 * page in a new tab; a hover tooltip lists the active incidents.
 */
export function OutageIndicator() {
  const { data, isLoading, isError } = useOutageStatus();

  if (isLoading || isError || !data?.outage) return null;

  const variant = VARIANT_BY_OVERALL[data.overall];

  return (
    <TooltipTrigger delay={150} placement="bottom">
      <a
        href={STATUS_PAGE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${data.label} — view status page`}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/40"
      >
        <StatusIndicator variant={variant} size="sm" pulse />
        <span>Service issue</span>
      </a>
      <Tooltip variant="light">
        <Flex direction="column" gap="xs" className="max-w-xs py-1">
          <Text size="sm" weight="semibold">
            {data.label}
          </Text>
          {data.incidents.map((incident) => (
            <Text key={incident.id || incident.title} size="xs" color="muted">
              {incident.title} · {INCIDENT_STATUS_LABEL[incident.status]}
            </Text>
          ))}
          <Text size="xs" color="muted">
            View status page →
          </Text>
        </Flex>
      </Tooltip>
    </TooltipTrigger>
  );
}
